import { WORKFLOW_CANVAS as C, WORKFLOW_FIRST_PATH } from "@sagot/constants";
import { exitSlotsOf, type ExitKey } from "../exit-slots";
import type { Definition } from "../types";
import { tileHeight, type Box, type Point } from "./connector-geometry";

export type { Box, Point } from "./connector-geometry";

/** The trigger tile's id; path tiles use their path letter. */
export const TRIGGER_TILE = "trigger";

/**
 * A connector from one exit of a step (or from the trigger: `step: -1`, no key) to the start of another tile. Every
 * connected exit has its own, so a step with three exits to one path gives three connectors.
 */
export interface CanvasEdge {
  id: string;
  from: string;
  to: string;
  step: number;
  key: ExitKey | null;
  /** A fixed exit (Start over, always back to Path A): drawn, but not a connection the author can select, move or delete. */
  fixed?: boolean;
}

/** The id of the connector of one exit. */
export const edgeId = (path: string, step: number, key: ExitKey): string => `${path}.${String(step)}.${key}`;

/** One connector per connected exit of every step, plus the trigger's line to Path A; an exit back to its own path draws nothing. */
export function edgesOf(definition: Definition): CanvasEdge[] {
  const edges: CanvasEdge[] = [];
  if (WORKFLOW_FIRST_PATH in definition.paths) edges.push({ id: `${TRIGGER_TILE}>${WORKFLOW_FIRST_PATH}`, from: TRIGGER_TILE, to: WORKFLOW_FIRST_PATH, step: -1, key: null });
  for (const [pathId, path] of Object.entries(definition.paths)) {
    path.steps.forEach((step, index) => {
      for (const slot of exitSlotsOf(step)) {
        if (!slot.target || slot.target === pathId || !(slot.target in definition.paths)) continue;
        edges.push({ id: edgeId(pathId, index, slot.key), from: pathId, to: slot.target, step: index, key: slot.key, ...(slot.fixed ? { fixed: true } : {}) });
      }
    });
  }
  return edges;
}

/**
 * The column of each path: Path A first, then one column further than the furthest path that leads to it. A path nothing
 * leads to sits in the second column so it stays visible; loops cannot push columns beyond the number of paths.
 */
export function columnsOf(definition: Definition): Record<string, number> {
  const ids = Object.keys(definition.paths);
  const columns: Record<string, number> = { [WORKFLOW_FIRST_PATH]: 0 };
  const edges = edgesOf(definition).filter((edge) => edge.from !== TRIGGER_TILE && !edge.fixed);
  let rounds = ids.length;
  for (let changed = true; changed && rounds > 0; rounds -= 1) {
    changed = false;
    for (const edge of edges) {
      const from = columns[edge.from];
      if (from === undefined) continue;
      const next = Math.min(from + 1, ids.length);
      if ((columns[edge.to] ?? -1) < next) {
        columns[edge.to] = next;
        changed = true;
      }
    }
  }
  for (const id of ids) columns[id] ??= 1;
  return columns;
}

const columnX = (column: number): number => C.padding + (column + 1) * (C.tileWidth + C.columnGap);

/** Where every tile sits when tidy: the trigger on the left, then the paths in columns, tiles of one column stacked. */
export function tidyLayout(definition: Definition, editable: boolean): Record<string, Box> {
  const columns = columnsOf(definition);
  const boxes: Record<string, Box> = { [TRIGGER_TILE]: { x: C.padding, y: C.padding, width: C.tileWidth, height: C.triggerHeight } };
  const bottoms = new Map<number, number>();
  for (const id of Object.keys(definition.paths).sort()) {
    const column = columns[id] ?? 1;
    const path = definition.paths[id];
    const height = path ? tileHeight(path, editable) : 0;
    const y = bottoms.get(column) ?? C.padding;
    boxes[id] = { x: columnX(column), y, width: C.tileWidth, height };
    bottoms.set(column, y + height + C.rowGap);
  }
  return boxes;
}

/** Tile positions after the author dragged some of them (`offsets` are added to the tidy position, never above or left of the canvas). */
export function withOffsets(boxes: Record<string, Box>, offsets: Readonly<Record<string, Point>>): Record<string, Box> {
  return Object.fromEntries(
    Object.entries(boxes).map(([id, box]) => {
      const offset = offsets[id];
      return [id, offset ? { ...box, x: Math.max(box.x + offset.x, 0), y: Math.max(box.y + offset.y, 0) } : box];
    }),
  );
}

/** The canvas is as large as the tiles plus a margin. */
export function canvasSize(boxes: Record<string, Box>): { width: number; height: number } {
  const all = Object.values(boxes);
  return {
    width: Math.max(...all.map((box) => box.x + box.width), 0) + C.padding,
    height: Math.max(...all.map((box) => box.y + box.height), 0) + C.padding,
  };
}

/** Where a line arrives: the left edge, level with the tile's header. */
export const targetPoint = (box: Box): Point => ({ x: box.x, y: box.y + C.headerHeight / 2 });

const MIN_CURVE = 40;

/** A smooth curve between two points; it leaves to the right and arrives from the left, also when the target lies behind. */
export function edgePath(from: Point, to: Point): string {
  const reach = Math.max(MIN_CURVE, Math.abs(to.x - from.x) / 2);
  return `M ${String(from.x)} ${String(from.y)} C ${String(from.x + reach)} ${String(from.y)}, ${String(to.x - reach)} ${String(to.y)}, ${String(to.x)} ${String(to.y)}`;
}

/** Clamps a zoom factor to the range the canvas supports, rounded to one step so repeated clicks do not drift. */
export function clampZoom(value: number): number {
  const stepped = Math.round(value / C.zoomStep) * C.zoomStep;
  return Math.min(C.zoomMax, Math.max(C.zoomMin, Number(stepped.toFixed(2))));
}
